/**
 * Permit Closeout Readiness — Phase 1F
 * ─────────────────────────────────────────────────────────────────────
 * Closes the final coordination gap after inspections pass: structured
 * closeout checklist + municipality closure metadata + readiness
 * derivation.
 *
 * Invariants:
 * 1. Optional until opened via `openPermitCloseout`. The presence of
 *    `permitDetail.permitCloseout` is the sole signal that closeout
 *    coordination has begun.
 * 2. Reversible item statuses: pending → complete → na → pending.
 *    `completedAt`/`completedBy` are stamped on `complete` and cleared
 *    on any other transition.
 * 3. Pure derivation only. `resolveCloseoutReadiness` never mutates the
 *    job, never advances permit stage, never auto-closes anything.
 * 4. NO automation, NO action queue events, NO notifications, NO
 *    municipality API, NO finance hooks, NO AI. Coordination only.
 */
import { z } from "zod";
import type { Job, PermitDetail } from "@/data/types";

export type PermitCloseoutItemStatus = "pending" | "complete" | "na";

export interface PermitCloseoutItem {
  id: string;
  label: string;
  status: PermitCloseoutItemStatus;
  completedAt?: string;
  completedBy?: string;
}

export interface PermitCloseout {
  owner?: string;
  municipalityClosureReference?: string;
  closureConfirmedDate?: string;
  notes?: string;
  items: PermitCloseoutItem[];
}

// ── Default checklist (deterministic shape) ─────────────────────────

interface DefaultItemSeed {
  id: string;
  label: string;
}

export const DEFAULT_CLOSEOUT_ITEMS: readonly DefaultItemSeed[] = [
  { id: "final-inspection-passed", label: "Final inspection passed" },
  { id: "inspection-evidence-saved", label: "Inspection evidence saved" },
  { id: "municipality-closure-confirmed", label: "Municipality closure confirmed" },
  { id: "permit-record-archived", label: "Permit card / permit record archived" },
  { id: "closeout-notes-reviewed", label: "Closeout notes reviewed" },
  { id: "job-closeout-handoff-ready", label: "Job closeout handoff ready" },
] as const;

// ── Validation ──────────────────────────────────────────────────────

const MAX_FREEFORM = 500;
const MAX_SHORT = 160;

export const closeoutMetadataSchema = z.object({
  owner: z.string().trim().max(MAX_SHORT).optional().or(z.literal("").transform(() => undefined)),
  municipalityClosureReference: z
    .string()
    .trim()
    .max(MAX_SHORT)
    .optional()
    .or(z.literal("").transform(() => undefined)),
  closureConfirmedDate: z
    .string()
    .datetime({ offset: true, message: "Closure date must be a full ISO datetime" })
    .optional()
    .or(z.literal("").transform(() => undefined)),
  notes: z.string().trim().max(MAX_FREEFORM).optional().or(z.literal("").transform(() => undefined)),
});

export type CloseoutMetadataInput = z.infer<typeof closeoutMetadataSchema>;

// ── Read accessors ──────────────────────────────────────────────────

export function getPermitCloseout(job: Job | null | undefined): PermitCloseout | null {
  return job?.permitDetail?.permitCloseout ?? null;
}

export function hasPermitCloseout(job: Job | null | undefined): boolean {
  return !!job?.permitDetail?.permitCloseout;
}

// ── Derivation ──────────────────────────────────────────────────────

export type CloseoutState = "not_started" | "not_ready" | "ready";
export type CloseoutTone = "neutral" | "amber" | "green";

export interface CloseoutReadiness {
  state: CloseoutState;
  tone: CloseoutTone;
  totalItems: number;
  /** Items not marked N/A — the active denominator. */
  applicableCount: number;
  completeCount: number;
  pendingCount: number;
  naCount: number;
  /** "X / Y complete" using the applicable denominator. */
  progressLabel: string;
  /** UI badge label. */
  badgeLabel: string;
}

export function resolveCloseoutReadiness(
  closeout: PermitCloseout | null | undefined,
): CloseoutReadiness {
  if (!closeout) {
    return {
      state: "not_started",
      tone: "neutral",
      totalItems: 0,
      applicableCount: 0,
      completeCount: 0,
      pendingCount: 0,
      naCount: 0,
      progressLabel: "Closeout not started",
      badgeLabel: "Not Started",
    };
  }
  const items = closeout.items;
  const total = items.length;
  const naCount = items.filter((i) => i.status === "na").length;
  const completeCount = items.filter((i) => i.status === "complete").length;
  const pendingCount = items.filter((i) => i.status === "pending").length;
  const applicable = total - naCount;

  // Empty checklist or all N/A: treat as not_ready (operator must
  // populate at least one applicable item to ever reach ready).
  if (applicable === 0) {
    return {
      state: "not_ready",
      tone: "amber",
      totalItems: total,
      applicableCount: 0,
      completeCount,
      pendingCount,
      naCount,
      progressLabel: total === 0 ? "0 / 0 complete" : "All items N/A",
      badgeLabel: "Closeout Pending",
    };
  }

  if (pendingCount === 0 && completeCount === applicable) {
    return {
      state: "ready",
      tone: "green",
      totalItems: total,
      applicableCount: applicable,
      completeCount,
      pendingCount,
      naCount,
      progressLabel: `${completeCount} / ${applicable} complete`,
      badgeLabel: "Ready to Close Permit",
    };
  }

  return {
    state: "not_ready",
    tone: "amber",
    totalItems: total,
    applicableCount: applicable,
    completeCount,
    pendingCount,
    naCount,
    progressLabel: `${completeCount} / ${applicable} complete`,
    badgeLabel: "Closeout Pending",
  };
}

// ── Mutators (sole writers) ─────────────────────────────────────────

function withPermitDetail(job: Job, mutate: (pd: PermitDetail) => PermitDetail): Job {
  if (!job.permitDetail) return job;
  return { ...job, permitDetail: mutate(job.permitDetail) };
}

function seedDefaultItems(): PermitCloseoutItem[] {
  return DEFAULT_CLOSEOUT_ITEMS.map((seed) => ({
    id: seed.id,
    label: seed.label,
    status: "pending" as const,
  }));
}

/**
 * Open closeout coordination. Idempotent: if a closeout already exists,
 * returns the job unchanged.
 */
export function openPermitCloseout(job: Job): Job {
  return withPermitDetail(job, (pd) => {
    if (pd.permitCloseout) return pd;
    return {
      ...pd,
      permitCloseout: {
        items: seedDefaultItems(),
      },
    };
  });
}

export function setCloseoutMetadata(job: Job, input: CloseoutMetadataInput): Job {
  const parsed = closeoutMetadataSchema.parse(input);
  return withPermitDetail(job, (pd) => {
    const current = pd.permitCloseout ?? { items: seedDefaultItems() };
    return {
      ...pd,
      permitCloseout: {
        ...current,
        owner: parsed.owner,
        municipalityClosureReference: parsed.municipalityClosureReference,
        closureConfirmedDate: parsed.closureConfirmedDate,
        notes: parsed.notes,
      },
    };
  });
}

/**
 * Set a single checklist item's status. Stamps `completedAt` /
 * `completedBy` when transitioning to `complete`; clears them on any
 * other status. Reversible.
 */
export function setCloseoutItemStatus(
  job: Job,
  itemId: string,
  status: PermitCloseoutItemStatus,
  by: string,
): Job {
  return withPermitDetail(job, (pd) => {
    if (!pd.permitCloseout) return pd;
    const items = pd.permitCloseout.items.map((i) => {
      if (i.id !== itemId) return i;
      if (status === "complete") {
        return {
          ...i,
          status,
          completedAt: new Date().toISOString(),
          completedBy: by,
        };
      }
      const { completedAt: _ca, completedBy: _cb, ...rest } = i;
      void _ca; void _cb;
      return { ...rest, status };
    });
    return {
      ...pd,
      permitCloseout: { ...pd.permitCloseout, items },
    };
  });
}

/**
 * Cycle helper: pending → complete → na → pending.
 */
export function cycleCloseoutItem(job: Job, itemId: string, by: string): Job {
  const closeout = getPermitCloseout(job);
  if (!closeout) return job;
  const item = closeout.items.find((i) => i.id === itemId);
  if (!item) return job;
  const next: PermitCloseoutItemStatus =
    item.status === "pending" ? "complete" :
    item.status === "complete" ? "na" :
    "pending";
  return setCloseoutItemStatus(job, itemId, next, by);
}
